import BaseService from './BaseService';
import socket from './socket';
import { getActorName } from 'shared/hooks/useActorName';
import { AgentsResponse, AlertRecipient, ApprovalRow, Command, Area, EventRow, HubHost, PipelinePrRow, RunField, RunRow, SavedAgentSettings, UsageReport, QualityReport } from 'pages/AgentHub/types';

function rethrow(err: any): never {
  throw new Error(err?.response?.data?.error ?? err?.message ?? 'request failed');
}

// GET/POST /agent-hub/* — the collector's agent hub routes (src/sbly-api-server/agentHub).
class AgentHubService extends BaseService {
  async agents(): Promise<AgentsResponse> {
    await this._getAccessToken();
    const res = await this.http.get('agent-hub/agents').catch(rethrow);
    return res.data;
  }

  /** Each hub on its own (engineers); agents() is them merged into one. */
  async hosts(): Promise<HubHost[]> {
    await this._getAccessToken();
    const res = await this.http.get('agent-hub/agents/hosts').catch(rethrow);
    return Array.isArray(res.data?.hosts) ? res.data.hosts : [];
  }

  /** How the manager agents scored each agent's runs: averages, weekly trends, worst runs, proposals. */
  async quality(days?: number): Promise<QualityReport> {
    await this._getAccessToken();
    const res = await this.http.get('agent-hub/run-scores/summary', { params: { days } }).catch(rethrow);
    return res.data;
  }

  /** What each agent consumed: today / 7 / 30 days, per PR and repo, with hints (built on request, kept 5 min). */
  async usage(): Promise<UsageReport> {
    await this._getAccessToken();
    const res = await this.http.get('agent-hub/usage').catch(rethrow);
    return res.data;
  }

  async runs(agent?: string, limit = 50): Promise<RunRow[]> {
    await this._getAccessToken();
    const res = await this.http.get('agent-hub/runs', { params: { agent, limit } }).catch(rethrow);
    return Array.isArray(res.data?.runs) ? res.data.runs : [];
  }

  async run(runId: string): Promise<{ run: RunRow; events: EventRow[]; children: RunRow[] }> {
    await this._getAccessToken();
    const res = await this.http.get(`agent-hub/runs/${encodeURIComponent(runId)}`).catch(rethrow);
    return res.data;
  }

  /** Pause, resume or run an agent now: answers once the hub has applied it, with the hub's new state. */
  async command(agent: string, command: Command): Promise<AgentsResponse['hub']> {
    await this._getAccessToken();
    const res = await this.http.post('agent-hub/commands', { agent, command }, { headers: { 'X-Sbly-Verified-User': getActorName() } }).catch(rethrow);
    return res.data?.hub ?? null;
  }

  async prs(open: boolean): Promise<PipelinePrRow[]> {
    await this._getAccessToken();
    const res = await this.http.get('agent-hub/prs', { params: { open: open ? '1' : undefined, limit: 100 } }).catch(rethrow);
    return Array.isArray(res.data?.prs) ? res.data.prs : [];
  }

  async prRuns(subject: string): Promise<RunRow[]> {
    await this._getAccessToken();
    const res = await this.http.get('agent-hub/prs/runs', { params: { subject } }).catch(rethrow);
    return Array.isArray(res.data?.runs) ? res.data.runs : [];
  }

  async approvals(status?: string): Promise<ApprovalRow[]> {
    await this._getAccessToken();
    const res = await this.http.get('agent-hub/approvals', { params: { status, limit: 100 } }).catch(rethrow);
    return Array.isArray(res.data?.approvals) ? res.data.approvals : [];
  }

  /**
   * The hub's live state, pushed over the app's socket as it changes (from Firebase, through the API).
   * Calls `onState` on joining and on every change; returns the unsubscribe. Rejoins after a reconnect.
   */
  async watch(onState: (hub: AgentsResponse['hub']) => void): Promise<() => void> {
    await this._getAccessToken();
    const join = () => socket.emit('joinAgentHub', { token: this.accessToken });
    socket.on('agentHubState', onState);
    socket.on('connect', join);
    if (socket.connected) join();
    return () => { socket.off('agentHubState', onState); socket.off('connect', join); };
  }

  /** An agent's settings saved on the Agents page (empty: its agent.yaml defaults). */
  async settings(agent: string): Promise<SavedAgentSettings> {
    await this._getAccessToken();
    const res = await this.http.get(`agent-hub/agents/${encodeURIComponent(agent)}/settings`).catch(rethrow);
    return res.data?.settings ?? {};
  }

  /** Saves who an agent's alerts @-mention (an empty list: back to the approvers); the hub applies it live. */
  async saveAlertRecipients(agent: string, alertRecipients: AlertRecipient[]): Promise<SavedAgentSettings> {
    await this._getAccessToken();
    const res = await this.http.put(`agent-hub/agents/${encodeURIComponent(agent)}/settings`, { alertRecipients }, { headers: { 'X-Sbly-Verified-User': getActorName() } }).catch(rethrow);
    return res.data?.settings ?? {};
  }

  /** Saves run settings: a field given overrides agent.yaml's, a null resets it to agent.yaml's; the hub applies them live. */
  async saveRunSettings(agent: string, run: Partial<Record<RunField, string | boolean | number | null>>): Promise<SavedAgentSettings> {
    await this._getAccessToken();
    const res = await this.http.put(`agent-hub/agents/${encodeURIComponent(agent)}/settings`, { run }, { headers: { 'X-Sbly-Verified-User': getActorName() } }).catch(rethrow);
    return res.data?.settings ?? {};
  }

  /** The hub's areas and who owns each (every agent reads them live). */
  async areas(): Promise<Area[]> {
    await this._getAccessToken();
    const res = await this.http.get('agent-hub/areas').catch(rethrow);
    return Array.isArray(res.data?.areas) ? res.data.areas : [];
  }

  /** Adds an area, with nobody assigned yet. */
  async addArea(name: string): Promise<Area> {
    await this._getAccessToken();
    const res = await this.http.post('agent-hub/areas', { name }, { headers: { 'X-Sbly-Verified-User': getActorName() } }).catch(rethrow);
    return res.data.area;
  }

  /** Sets who owns an area (any number of people; an empty list leaves it unowned). */
  async setAreaPeople(id: string, people: AlertRecipient[]): Promise<Area> {
    await this._getAccessToken();
    const res = await this.http.put(`agent-hub/areas/${encodeURIComponent(id)}`, { people }, { headers: { 'X-Sbly-Verified-User': getActorName() } }).catch(rethrow);
    return res.data.area;
  }

  async removeArea(id: string): Promise<void> {
    await this._getAccessToken();
    await this.http.delete(`agent-hub/areas/${encodeURIComponent(id)}`, { headers: { 'X-Sbly-Verified-User': getActorName() } }).catch(rethrow);
  }

  /** The Slack workspace's people, to pick an agent's alert recipients from. */
  async slackMembers(): Promise<AlertRecipient[]> {
    await this._getAccessToken();
    const res = await this.http.get('agent-hub/slack-members').catch(rethrow);
    return Array.isArray(res.data?.members) ? res.data.members : [];
  }

  /** Give an agent a task; it does it on its next free slot. */
  async trigger(agent: string, task: string): Promise<void> {
    await this._getAccessToken();
    await this.http.post('agent-hub/trigger', { agent, task, source: 'agents-page' }, { headers: { 'X-Sbly-Verified-User': getActorName() } }).catch(rethrow);
  }
}

export default AgentHubService;
